// Resume content — the single source of truth for everything rendered on the page.
// Edit this file to update the resume; components below the data layer are pure presentation.
//
// The shape is intentionally small and explicit. If you add a new section,
// extend the `Resume` type and create a matching component under
// `src/components/sections/`.

export type ExperienceEntry = {
  role: string;
  company: string;
  period: string;
  bullets: string[];
};

export type ProjectEntry = {
  name: string;
  description?: string;
  stack?: string;
  github?: string;
  app?: string;
};

export type ResumeLinks = {
  email: string;
  github: string;
  linkedin: string;
};

export type Resume = {
  name: string;
  status: string;
  title: string;
  tagline: string;
  links: ResumeLinks;
  about: string;
  experience: ExperienceEntry[];
  skills: string[];
  projects: ProjectEntry[];
};

export const resume: Resume = {
  name: 'ANTON MARKAU',
  status: 'Open for hire',
  title: 'FULL STACK WEB DEVELOPER',
  tagline: 'TypeScript • Node.js • React • AWS • PostgreSQL',
  links: {
    email: 'antonmarkau.93@gmail.com',
    github: 'https://github.com/antonmarkau',
    linkedin: 'https://linkedin.com/in/anton-markau-076250207',
  },
  about:
    'Full-stack engineer who likes owning a feature end-to-end — from the database schema and ' +
    'the API, through the data pipelines and orchestration, all the way to the React UI. ' +
    'I gravitate toward TypeScript, PostgreSQL, and AWS, and I enjoy turning fuzzy product ' +
    'requirements into reliable, observable systems that are pleasant to operate.',
  experience: [
    {
      role: 'Software Development Engineer — Map Feedback Tooling',
      company: 'Mapbox',
      period: 'Dec 2021 — Jun 2026',
      bullets: [
        'Lead developer of the internal service for editing road data (NestJS, AWS ECS, S3, PostgreSQL): ' +
          'iteration lifecycle, geometry-based task splitting, road-graph import jobs, and integration ' +
          'with the downstream correction engine for edits publishing.',
        'Designed and shipped public facing tool for viewing user\'s submitted feedback and its status end-to-end — a Hono + Kysely + PostgreSQL API on ' +
          'AWS Lambda/SQS/EventBridge, paired with a React app ' +
          '(Redux Toolkit, Mapbox GL, Konva). Covers feedback preview, search, filtering, file & ' +
          'screenshot attachments.',
        'Built the in-app user feedback flow used across the Mapbox Console: draggable feedback form modal, ' +
          'category selector, drag-and-drop attachments, and a Konva-based screenshot editor with ' +
          'annotation and text tools.',
        'Own a fleet of ECS jobs and Airflow DAGs for saving internal data/statistics, feedback exports ' +
          'to AWS Glue/Athena, and cross-service monitoring used by the wider map-data org.',
        'Created and actively maintained various Mode Analytics reports for internal stakeholders, including a real-time feedback volume dashboard ' +
          'and a data quality report for monitoring the health of our map data.',
        'Regular on-call: vulnerability remediation, Docker/IAM/RDS infra fixes, CloudWatch & ' +
          'Sumologic debugging, and CloudFormation stacks maintenance.',
      ],
    },
  ],
  skills: [
    'TypeScript',
    'Node.js',
    'React',
    'NestJS',
    'Hono',
    'Express',
    'Redux Toolkit',
    'Mapbox GL JS',
    'PostgreSQL',
    'Kysely',
    'TypeORM',
    'AWS (ECS, S3, SQS, Lambda)',
    'Cloud Formation',
    'Airflow',
    'Docker',
    'Nx monorepos',
    'Vitest / Jest',
    'Playwright',
    'GitHub Actions',
    'REST APIs',
    'GeoJSON / OSM data',
  ],
  projects: [
    {
      name: 'Pathfinder',
      description: 'The web app for finding an optimal route between multiple points on the map',
      github: 'https://github.com/antonmarkau/pathfinder',
      app: 'https://pathfinder-aird.onrender.com/',
    },
  ],
};
